import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/SEO";
import { api, formatApiError, COUNTRY_FLAGS, COUNTRY_LABELS } from "@/lib/api";
import { Search, Building2, BadgeCheck, MapPin, Home, ArrowRight, Filter } from "lucide-react";
import { toast } from "sonner";

const SORTS = [
  { key: "listings", label: "Plus actives" },
  { key: "recent", label: "Plus récentes" },
  { key: "alpha", label: "A → Z" },
];

export default function Agencies() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [verified, setVerified] = useState(false);
  const [sort, setSort] = useState("listings");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { sort, limit: 50 };
        if (q) params.q = q;
        if (country) params.country = country;
        if (city) params.city = city;
        if (verified) params.verified = true;
        const { data } = await api.get("/agencies", { params });
        if (!cancelled) {
          setItems(data.items);
          setTotal(data.total);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, country, city, verified, sort]);

  return (
    <Layout>
      <SEO
        title="Annuaire des agences immobilières du Maghreb · 3aqarate"
        description="Trouvez les meilleures agences immobilières au Maroc, Algérie et Tunisie. Toutes nos agences Pro sont vérifiées."
      />

      <section className="bg-[#F5F0EB] border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Annuaire</span>
          <h1 className="font-heading text-4xl md:text-6xl font-medium tracking-tight mt-2 mb-3">
            Toutes les agences immobilières du Maghreb
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            {total} agence{total > 1 ? "s" : ""} vérifiée{total > 1 ? "s" : ""} prête{total > 1 ? "s" : ""} à vous accompagner au Maroc, en Algérie et en Tunisie.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4 mb-6 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="agencies-filters">
          <div className="md:col-span-4 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom ou société..."
              data-testid="agencies-search-input"
              className="pl-10 rounded-xl"
            />
          </div>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="agencies-country-filter"
            className="md:col-span-2 h-10 px-3 rounded-xl border border-[#E5E5E5] text-sm bg-white"
          >
            <option value="">Tous pays</option>
            <option value="MA">🇲🇦 Maroc</option>
            <option value="DZ">🇩🇿 Algérie</option>
            <option value="TN">🇹🇳 Tunisie</option>
          </select>
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Ville (ex: Casablanca)"
            data-testid="agencies-city-filter"
            className="md:col-span-2 rounded-xl"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="agencies-sort"
            className="md:col-span-2 h-10 px-3 rounded-xl border border-[#E5E5E5] text-sm bg-white"
          >
            {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <label className="md:col-span-2 flex items-center gap-2 text-xs px-3 rounded-xl border border-[#E5E5E5] bg-white cursor-pointer hover:border-[#D6202C]/40">
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
              data-testid="agencies-verified-filter"
              className="accent-[#11783A]"
            />
            <BadgeCheck className="w-3.5 h-3.5 text-[#11783A]" /> Pro vérifiées
          </label>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agencies-loading">
            {[0,1,2,3,4,5].map(i => <div key={i} className="h-44 rounded-2xl bg-[#F5F0EB] animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#E5E5E5] p-16 text-center" data-testid="agencies-empty">
            <Building2 className="w-12 h-12 text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-muted-foreground">Aucune agence ne correspond à votre recherche.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agencies-grid">
            {items.map((a) => (
              <AgencyCard key={a.id} agency={a} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const AgencyCard = ({ agency }) => (
  <Link
    to={`/agence/${agency.id}`}
    data-testid={`agency-card-${agency.id}`}
    className="group bg-white rounded-2xl border border-[#E5E5E5] p-5 hover:border-[#D6202C] hover:shadow-md transition flex flex-col"
  >
    <div className="flex items-start gap-3 mb-3">
      <div className="w-14 h-14 rounded-2xl bg-[#11783A] text-white flex items-center justify-center font-heading text-2xl shrink-0 overflow-hidden">
        {agency.picture ? (
          <img src={agency.picture} alt={agency.name} className="w-full h-full object-cover" />
        ) : (
          (agency.company || agency.name).charAt(0)
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-lg font-medium leading-tight truncate group-hover:text-[#D6202C] transition-colors flex items-center gap-1">
          {agency.company || agency.name}
          {agency.badge === "pro_verified" && <BadgeCheck className="w-4 h-4 text-[#11783A] shrink-0" />}
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
          {COUNTRY_FLAGS[agency.country]} {COUNTRY_LABELS[agency.country]}
        </div>
      </div>
    </div>

    <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
      <Home className="w-3.5 h-3.5" /> <strong className="text-[#1A1A1A] tabular-nums">{agency.listings_count}</strong> annonce{agency.listings_count > 1 ? "s" : ""} active{agency.listings_count > 1 ? "s" : ""}
    </div>

    {agency.sample_cities?.length > 0 && (
      <div className="text-xs text-muted-foreground flex items-start gap-1.5 mb-3 flex-wrap">
        <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{agency.sample_cities.join(" · ")}</span>
      </div>
    )}

    <div className="mt-auto pt-3 border-t border-[#F5F0EB] text-xs text-[#D6202C] flex items-center gap-1 group-hover:gap-2 transition-all">
      Voir l'agence <ArrowRight className="w-3.5 h-3.5" />
    </div>
  </Link>
);
